import { Link, Outlet, useParams } from 'react-router-dom';
import { BookOpen } from 'lucide-react';

import { guideBySlug } from '@/data/knowledgeBase';
import { AppRoutes } from '@/constants/routes';
import { GuideArticle, GuideNotFound } from './GuidePage';
import { KnowledgeBasePage } from './KnowledgeBasePage';

/**
 * The public mirror of the knowledge base, at /kb.
 *
 * Sits outside the authenticated route tree, so the pages below render for someone with no account
 * at all — a new hire before their user exists, an external accountant, an auditor asking how a
 * control works. That is the whole point: an in-app /help link sent to such a person lands on the
 * login screen and teaches them nothing.
 *
 * Nothing here reads company data. The guides are static content compiled into the bundle, and the
 * screen links inside them are permission-gated, so a signed-out reader is offered none of them.
 * Sharing a guide therefore discloses how myLedger works, never what is in anyone's ledger.
 */

const PublicShell = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
    <header className="border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-3">
        <Link to={AppRoutes.KB_PUBLIC}
          className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
          <BookOpen className="h-4 w-4 text-brand-600 dark:text-brand-400" />
          myLedger ONE · Knowledge base
        </Link>
        <Link to={AppRoutes.LOGIN}
          className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brand-700">
          Sign in
        </Link>
      </div>
    </header>

    <main className="mx-auto max-w-6xl">{children}</main>

    <footer className="mx-auto max-w-6xl px-6 pb-10 pt-4 text-xs text-slate-400">
      Reference documentation only — no company or transaction data appears on these pages.
    </footer>
  </div>
);

/** Layout route: the shell, with the index or a guide rendered inside it. */
export const PublicKnowledgeBaseLayout = () => (
  <PublicShell><Outlet /></PublicShell>
);

export const PublicKnowledgeBasePage = () => <KnowledgeBasePage variant="public" />;

export const PublicGuidePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const guide = slug ? guideBySlug(slug) : undefined;

  return (
    <div className="p-6">
      {guide
        ? <GuideArticle
            guide={guide}
            indexHref={AppRoutes.KB_PUBLIC}
            guideHref={s => AppRoutes.KB_PUBLIC_GUIDE.replace(':slug', s)}
            shareable={false}
            publicView />
        : <GuideNotFound indexHref={AppRoutes.KB_PUBLIC} />}
    </div>
  );
};
